import "../JSONTools/JSONformatter.css";
import ToolHeader from "../../Components/ToolHeader";
import { useState } from "react";
import "./Argon2Hash.css";
import { Row, Col } from "react-bootstrap";

import { ReactComponent as CopyIcon } from "../../Icons/Copy.svg";
import { ReactComponent as RegenerateIcon } from "../../Icons/Regenerate.svg";
import { ReactComponent as ShowPasswordIcon } from "../../Icons/eye.svg";
import { ReactComponent as HidePasswordIcon } from "../../Icons/eye-slash.svg";
import { Argon2HashGenerate, Argon2HashVerify, Argon2SaltGenerate } from "../../Services/APIService";

const variants = [
  { name: "Argon2id", value: "Argon2id", description: "Recommended" },
  { name: "Argon2i", value: "Argon2i", description: "Side-Channel Safe" },
  { name: "Argon2d", value: "Argon2d", description: "GPU Resistant" },
];

const presets = {
  Interactive: { mem: 4096, time: 3, parallelism: 1, hashLen: 64 },
  Moderate: { mem: 65536, time: 3, parallelism: 4, hashLen: 64 },
  Sensitive: { mem: 262144, time: 5, parallelism: 8, hashLen: 64 },
  Custom: { mem: 0, time: 0, parallelism: 0, hashLen: 0 },
};

const presetTooltipData = {
  Interactive: { useCase: "Interactive login", memory: "4 MB", time: 3, parallelism: 1 },
  Moderate: { useCase: "Web services", memory: "64 MB", time: 3, parallelism: 4 },
  Sensitive: { useCase: "High-value secrets", memory: "256 MB", time: 5, parallelism: 8 },
};


const Argon2Hash = () => {
  const [password, setPassword] = useState("");
  const [salt, setSalt] = useState("73c2a99f3dfd6c92fe9e13da25772ce5");
  const [variant, setVariant] = useState(variants[2]);

  const [preset, setPreset] = useState("Interactive");
  const [memoryCost, setMemoryCost] = useState(presets.Interactive.mem);
  const [timeCost, setTimeCost] = useState(presets.Interactive.time);
  const [parallelism, setParallelism] = useState(presets.Interactive.parallelism);
  const [hashLength, setHashLength] = useState(presets.Interactive.hashLen);
  const [encodedHash, setEncodedHash] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rawHash, setRawHash] = useState("");
  const [hashProcessingTime, setHashProcessingTime] = useState("");
  const [verifyprocessingTime, setVerifyProcessingTime] = useState("");
  const [generateMessage, setGenerateMessage] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [verifyEncoded, setVerifyEncoded] = useState("");
  const [verifyResult, setVerifyResult] = useState("");
  const [encodesHashCopied, setEncodesHashCopied] = useState(false);
  const [rawHashCopied, setRawHashCopied] = useState(false);
  const toolFav = { toolName: "Argon2 Hasher", Iconpath: "/FileCodeRaw.svg" };

  

  

  const selectPreset = (key) => {
    const values = presets[key];
    setPreset(key);
    setMemoryCost(values.mem);
    setTimeCost(values.time);
    setParallelism(values.parallelism);
    setHashLength(values.hashLen);
  };

  const handleGenerate = async () => {
    if (!password || !salt) {
      setEncodedHash("");
      setRawHash("");
      setHashProcessingTime("");
      setGenerateMessage({ text: "Please enter both password and salt.", type: "error" });
      return;
    }

    try {
      const payload = preset === "Custom" ? {
        password: password,
        variant: variant.value,
        preset: preset,
        memoryKb: memoryCost,
        iterations: timeCost,
        parallelism: parallelism,
        hashLength: hashLength
      } : {
        password: password,
        variant: variant.value,
        preset: preset
      }
      const response = await Argon2HashGenerate(payload);
      setEncodedHash(response.data.phc);
      setRawHash(response.data.hash);
      setGenerateMessage({ text: "Hash generated", type: "success" });
      setHashProcessingTime(response.data.processingTime);
    } catch (error) {
      setEncodedHash("");
      setRawHash("");
      setHashProcessingTime("");
      setGenerateMessage({ text: `Error generating Argon2 hash: ${error.message}`, type: "error" });
    }
  };



  const generateSalt = async () => {
    try {
      const response = await Argon2SaltGenerate();
      setSalt(response.data.saltHex);
    } catch (error) {
      setGenerateMessage({ text: `Error generating salt: ${error.message}`, type: "error" });
    }
  }

  const handleVerify = async () => {
    if (!verifyPassword || !verifyEncoded) {
      setVerifyResult({ valid: false, message: "Enter password and encoded hash to verify." });
      return;
    }
    try {
      const payload = {
        password: verifyPassword,
        hash: verifyEncoded
      };
      const response = await Argon2HashVerify(payload);
      setVerifyResult({ valid: response.data.match, message: "Verified! password matches the hash" });
      setVerifyProcessingTime(response.data.processingTimeMs);
    } catch (error) {
      setVerifyResult({ valid: false, message: "Verification failed: " + (error.message || error.code || "invalid hash") });
    }
  };

  const copyText = async (text, type) => {
    if (!text) return;
    if (type === "encoded") {
      setEncodesHashCopied(true);
      setTimeout(() => setEncodesHashCopied(false), 2000);
    } else if (type === "raw") {
      setRawHashCopied(true);
      setTimeout(() => setRawHashCopied(false), 2000);
    }
    await navigator.clipboard.writeText(text);
  };

  const setPasswordVisibility = (visible) => {
    setShowPassword(visible);
  };

  const renderInfoIcon = (tooltipText) => (
    <span className="info-tooltip-wrapper">
      <button
        type="button"
        className="info-icon"
      >
        i
      </button>
      <span className="bcrypt-tooltip">{tooltipText}</span>
    </span>
  );

  const renderPresetTooltip = (key) => {
    const values = presetTooltipData[key] || {
      useCase: "Manual tuning",
      memory: memoryCost ? `${Math.round(memoryCost / 1024)} MB` : "--",
      time: timeCost || "--",
      parallelism: parallelism || "--",
    };

    return (
      <span className="preset-tooltip">
        <span className="preset-tooltip-title">{key} &mdash;</span>
        <span className="preset-tooltip-subtitle">Recommended Parameters</span>
        <span className="preset-tooltip-grid">
          <span className="preset-tooltip-heading">Use Case</span>
          <span className="preset-tooltip-heading">Memory</span>
          <span className="preset-tooltip-heading">Time</span>
          <span className="preset-tooltip-heading">Parallel</span>
          <span>{values.useCase}</span>
          <span>{values.memory}</span>
          <span>{values.time}</span>
          <span>{values.parallelism}</span>
        </span>
      </span>
    );
  };

  const resetForm = () => {
    setPassword("");
    setSalt("");
    setEncodedHash("");
    setRawHash("");
    setHashProcessingTime("");
    setGenerateMessage("");
    setVerifyPassword("");
    setVerifyEncoded("");
    setVerifyResult(null);
    selectPreset("Interactive");
  };

  return (
    <>
      <Col>
        <ToolHeader title="Argon2 Hasher" description="Generate and verify Argon2 hashes with tuned memory, time, parallelism and hash length." toolFav={toolFav} />
        <Row className="argon2-grid">
          <Col>
            <div className="argon2-card-header">
              <span>Generate Argon2 Hash</span>
            </div>
            <div className="argon2-card">
              <label className="Argonoutput-label">Algorithm Variant &nbsp;
                {renderInfoIcon("Algorithm variant Argon2id is the recommended general-purpose variant.")}
              </label>
              <div className="argon-variant-selector" role="radiogroup" aria-label="Algorithm Variant">
                {variants.map((algo) => (
                  <button
                    key={algo.value}
                    type="button"
                    role="radio"
                    aria-checked={variant.value === algo.value}
                    className={`argon-variant-option ${variant.value === algo.value ? "active" : ""}`}
                    onClick={() => setVariant(algo)}
                  >
                    <span className="argon-variant-name">{algo.name}</span>
                    <span className="argon-variant-description">{algo.description}</span>
                  </button>
                ))}
              </div>

              <label className="Argonoutput-label">Password &nbsp;
                {renderInfoIcon("The plaintext password to hash.")}
              </label>
              <div className="argon-password-row">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter Password..."
                  className="argonPassword"
                />
                <div className="password-toggle-icon">
                  {showPassword ? (
                    <HidePasswordIcon className="headerIcon" onClick={() => setPasswordVisibility(false)} />
                  ) : (
                    <ShowPasswordIcon className="headerIcon" onClick={() => setPasswordVisibility(true)} />
                  )}
                </div>
              </div>
              <label className="Argonoutput-label">Salt (hex) &nbsp;
                {renderInfoIcon("The plaintext password to hash.")}
              </label>
              <div className="argon-salt-row">
                <input
                  value={salt}
                  onChange={(e) => setSalt(e.target.value)}
                  placeholder="Enter salt in hex format"
                />
                <div className="regenerate-button"
                  onClick={() => generateSalt()}>
                  <RegenerateIcon className="headerIcon" /> &nbsp;
                  <span>Regenerate</span>
                </div>
              </div>

              <label className="Argonoutput-label">Security Presets</label>
              <div className="preset-row">
                {Object.keys(presets).map((key) => (
                  <span className="preset-tooltip-wrapper" key={key}>
                    <button
                      type="button"
                      className={preset === key ? "preset-button active" : "preset-button"}
                      onClick={() => selectPreset(key)}
                    >
                      {key}
                    </button>
                    {renderPresetTooltip(key)}
                  </span>
                ))}
              </div>


              <div className="preset-panel">
                <div className="preset-title">Security Presets</div>

                {/* Memory Cost */}
                <div className="slider-group">
                  <div className="row-head">
                    <span>Memory Cost (KB)</span>
                    <span className="value-text">{memoryCost}</span>
                  </div>

                  <input
                    className="custom-slider"
                    type="range"
                    min={1024}
                    max={262144}
                    step={1024}
                    value={memoryCost}
                    onChange={(e) => setMemoryCost(Number(e.target.value))}
                  />

                  <div className="sub-note">
                    {(memoryCost / 1024).toFixed(0)} MB default
                  </div>
                </div>

                {/* Time Cost */}
                <div className="slider-group">
                  <div className="row-head">
                    <span>Time Cost (Iterations)</span>
                    <span className="value-text">{timeCost}</span>
                  </div>

                  <input
                    className="custom-slider"
                    type="range"
                    min={1}
                    max={10}
                    step={1}
                    value={timeCost}
                    onChange={(e) => setTimeCost(Number(e.target.value))}
                  />
                </div>

                {/* Parallelism */}
                <div className="slider-group">
                  <div className="row-head">
                    <span>Parallelism</span>
                    <span className="value-text">{parallelism}</span>
                  </div>

                  <input
                    className="custom-slider"
                    type="range"
                    min={1}
                    max={16}
                    step={1}
                    value={parallelism}
                    onChange={(e) => setParallelism(Number(e.target.value))}
                  />
                </div>

                {/* Hash Length */}
                <div className="slider-group">
                  <div className="row-head">
                    <span>Hash Length (bytes)</span>
                    <span className="value-text">{hashLength}</span>
                  </div>

                  <input
                    className="custom-slider"
                    type="range"
                    min={16}
                    max={64}
                    step={1}
                    value={hashLength}
                    onChange={(e) => setHashLength(Number(e.target.value))}
                  />
                </div>
              </div>

              <div className="argon2-format-row">
                <button className="argon2-submit" onClick={handleGenerate}>
                  Generate Argon2 Hash
                </button>
                <button className="argon2-reset" onClick={resetForm}>
                  Reset
                </button>
              </div>
            </div>
          </Col>

          <Col>
            <div className="argon2-card-header">
              <span>Hash Created (Output)</span>
            </div>
            <div className="argon2-card">
              {generateMessage?.type === "success" && (
                <div className="verify-status-box success">
                  <div className="verify-line">
                    <span className="verify-icon"> ✓ </span>
                    <span className="verify-text">
                      {generateMessage.text}
                    </span>
                  </div>
                  <div className="verify-time">
                    Computed in {hashProcessingTime}s
                  </div>
                </div>
              )}
              {generateMessage?.type === "error" && (
                <div className="verify-status-box error">
                  <div className="verify-line">
                    <span className="verify-icon"> ✗ </span>
                    <span className="verify-text">
                      {generateMessage.text}
                    </span>
                  </div>
                </div>
              )}
              <div className="output-header">
                <label className="Argonoutput-label">Encoded Hash &nbsp;
                  {renderInfoIcon("PHC string format with all parameters embedded - use this to verify.")}
                </label>
                <div style={{display:"flex", alignItems:"center", gap:"5px", cursor:"pointer", fontSize:"12px"}}
                 onClick={() => copyText(encodedHash, "encoded")}>
                  <CopyIcon className="headerIcon" /> 
                  {encodesHashCopied ? "Copied!" : "Copy"}
                </div>
              </div>
              <textarea
                value={encodedHash || "--"}
                readOnly
                className="argon2-result-box"
              />
              <div className="output-header">
                <div className="Argonoutput-label">Raw hash Hex &nbsp;
                  {renderInfoIcon("The raw derived bytes in hexadecimal.")}
                </div>
                <div style={{display:"flex", alignItems:"center", gap:"5px", cursor:"pointer" , fontSize:"12px"}}
                 onClick={() => copyText(rawHash, "raw")}>
                  <CopyIcon className="headerIcon" /> 
                  {rawHashCopied ? "Copied!" : "Copy"}
                </div>
              </div>
              <textarea
                value={rawHash || "--"}
                readOnly
                className="argon2-result-box"
              />
            </div>
            <div className="argon2-card-header">
              <span>Verify Argon2 Hash</span>
            </div>
            <div className="argon2-card">
              <label className="Argonoutput-label">Password to Verify</label>
              <input
                type="password"
                value={verifyPassword}
                onChange={(e) => setVerifyPassword(e.target.value)}
                placeholder="Enter Password..."
              />

              <label>Encoded Hash &nbsp;
                {renderInfoIcon("Paste the PHC-format encoded hash to verify against.")}
              </label>
              <textarea
                value={verifyEncoded}
                onChange={(e) => setVerifyEncoded(e.target.value)}
                placeholder="Paste encoded Argon2 hash"
              />

              <button className="argon2-submit" onClick={handleVerify}>
                Verify Hash
              </button>

              {verifyResult && <div className={`verify-status-box ${verifyResult?.valid ? "success" : "error"}`}>
                <div className="verify-line">
                  <span className="verify-icon">{verifyResult?.valid ? "✓" : "✗"}</span>
                  <span className="verify-text">
                    {verifyResult?.valid ? "Verified ! Password matches the hash." : "Verification failed. Password does not match the hash."}
                  </span>
                </div>

                <div className="verify-time">
                  Computed in {verifyprocessingTime}s
                </div>
              </div>}
            </div>
          </Col>
        </Row >
      </Col >
    </>
  );
};

export default Argon2Hash;
